import React, { useState } from 'react';
import { useLang } from '@/context/LanguageContext';
import { Stars } from '@/components/Stars';
import { LegalModal } from '@/components/LegalModal';
import { proximaText } from '@/data/catalog';
import { Instagram, Facebook, Youtube } from 'lucide-react';
import marca from '@/assets/marca.png';

export const Footer = () => {
  const { t, lang } = useLang();
  const proxima = proximaText(lang);
  const [activeDoc, setActiveDoc] = useState(null);

  const socials = [
    { name: 'Instagram', icon: Instagram, href: 'https://www.instagram.com/canilloescenicarts/' },
    { name: 'Facebook', icon: Facebook, href: 'https://www.facebook.com/canilloscenicarts' },
    { name: 'YouTube', icon: Youtube, href: 'https://www.youtube.com/@canilloescenicarts/videos' },
  ];

  const legal = [
    { label: t.footer.legal, key: 'legal', testid: 'footer-legal-notice' },
    { label: t.footer.privacy, key: 'privacy', testid: 'footer-legal-privacy' },
    { label: t.footer.cookies, key: 'cookies', testid: 'footer-legal-cookies' },
  ];

  return (
    <footer data-testid="footer" className="relative overflow-hidden border-t border-[var(--cea-border)] bg-[#040f0c]">
      <Stars count={24} seed={9} />
      <div className="relative z-10 max-w-6xl mx-auto px-4 sm:px-6 py-14">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-10">
          {/* Brand */}
          <div className="md:col-span-5">
            <img src={marca} alt="Canillo Escènic Arts" className="h-24 w-auto mb-4" style={{ filter: 'none' }} />
            <div className="flex items-center gap-3" data-testid="footer-socials">
              {socials.map((s) => (
                <a
                  key={s.name}
                  href={s.href}
                  target="_blank"
                  rel="noreferrer"
                  aria-label={s.name}
                  data-testid={`footer-social-${s.name.toLowerCase()}`}
                  className="grid h-10 w-10 place-items-center rounded-full border border-[var(--cea-border)] bg-[#0b241b] text-white/80 hover:text-[#040f0c] hover:bg-[var(--cea-accent)] hover:border-[var(--cea-accent)] transition-colors duration-200"
                >
                  <s.icon size={18} />
                </a>
              ))}
            </div>
            <p className="mt-5 text-sm text-white/70 flex items-center gap-2">
              <span className="h-1.5 w-1.5 rounded-full bg-[var(--cea-accent)]" /> {t.hero.place}
            </p>
            {proxima && (
              <p className="mt-4 text-sm text-white/70" data-testid="footer-proxima">
                <span className="font-semibold text-[var(--cea-accent)]">{proxima.etiqueta}</span>
                <br />
                {proxima.dates}
              </p>
            )}
          </div>

          {/* Legal. La navegación del pie se retiró al vaciar la portada:
              las secciones a las que apuntaba ya no existen. */}
          <div className="md:col-span-4 md:col-start-9">
            <h4 className="font-display font-medium uppercase tracking-wide text-sm text-[var(--cea-accent)] mb-4">{t.footer.legalTitle}</h4>
            <ul className="space-y-2.5">
              {legal.map((l) => (
                <li key={l.testid}>
                  <button
                    data-testid={l.testid}
                    onClick={() => setActiveDoc(l.key)}
                    className="text-sm text-white/75 hover:text-[var(--cea-accent)] transition-colors duration-200 text-left"
                  >
                    {l.label}
                  </button>
                </li>
              ))}
            </ul>
          </div>
        </div>

        <div className="mt-12 pt-6 border-t border-white/10 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 text-xs text-white/50">
          <p>© 2026 Canillo Escènic Arts · {t.footer.rights}</p>
        </div>
      </div>
      <LegalModal docKey={activeDoc} onClose={() => setActiveDoc(null)} />
    </footer>
  );
};
